import React, { useRef, useState } from "react";
import { Button, ButtonProps } from "@mui/material";
import CloudUploadOutlinedIcon from "@mui/icons-material/CloudUploadOutlined";
import { styled } from "@mui/material/styles";
import { useHistory } from "react-router-dom";
import RoutesName from "routes/routesName";
import { useTypedSelector } from "hooks/store";
import { useCmrAuth } from "state/CMRAuth";
import Header from "../../components/AppHeader";
import Footer from "../../components/AppFooter";

const OutlineButton = styled(Button)<ButtonProps>(() => ({
    boxShadow: "none",
    textTransform: "none",
    fontSize: 18,
    fontWeight: "bold",
    border: "1px solid",
    color: "#E6117B",
    borderRadius: "50px",
    lineHeight: 1.5,
    backgroundColor: "#fff",
    borderColor: "#E6117B",
    fontFamily: [
        "Open Sans",
        "-apple-system",
        '"Helvetica Neue"',
        "Arial",
        "sans-serif",
        '"Apple Color Emoji"',
        '"Segoe UI Emoji"',
    ].join(","),
    "&:hover": {
        backgroundColor: "#BE0F66",
        borderColor: "#BE0F66",
        boxShadow: "none",
        color: "#fff",
    },
    "&:active": {
        boxShadow: "none",
        backgroundColor: "#BE0F66",
        borderColor: "#BE0F66",
    },
    "&:focus": {
        boxShadow: "0 0 0 0.2rem rgba(230, 17,123,.2)",
    },
}));

const VisuallyHiddenInput = styled("input")({
    clip: "rect(0 0 0 0)",
    clipPath: "inset(50%)",
    height: 1,
    overflow: "hidden",
    position: "absolute",
    bottom: 0,
    left: 0,
    whiteSpace: "nowrap",
    width: 1,
});

const ImportPage: React.FC = () => {
    const router = useHistory();
    const fileInputRef = useRef<HTMLInputElement | null>(null);

    const user = useTypedSelector((store) => store.CMRAuth.user);
    const cmrAuth = useCmrAuth();

    const [error, setError] = useState<string>("");

    const handleFileUpload = (event: React.ChangeEvent<HTMLInputElement>) => {
        const file = event.target.files?.[0];
        setError("");

        if (!file) return;

        if (!file.name.endsWith(".json")) {
            setError("Please upload a valid JSON file.");
            return;
        }

        const reader = new FileReader();

        reader.onload = async (e) => {
            try {
                const parsed = JSON.parse(e.target?.result as string);

                if (!parsed?.name || !parsed?.patientPosition) {
                    setError("Invalid scene format.");
                    return;
                }

                const sceneName = file.name.replace(/\.json$/i, "");
                const token = await cmrAuth.getAccessToken();

                const displayName = user?.name;

                const sceneRecord = {
                    sceneName,
                    data: parsed,
                    displayName,
                };

                const res = await fetch(`${process.env.REACT_APP_API_URL}/api/import`, {
                    method: "POST",
                    headers: {
                        Authorization: `Bearer ${token}`,
                        "Content-Type": "application/json",
                    },
                    body: JSON.stringify(sceneRecord),
                });

                const { id } = await res.json();

                router.push({
                    pathname: RoutesName.modelPage,
                    state: { sceneKey: id },
                });
            } catch {
                setError("There was a problem uploading your scene, please try again.");
            }
        };

        reader.readAsText(file);
    };

    const handleButtonClick = () => {
        fileInputRef.current?.click();
    };

    return (
        <div className="newSetupPage">
            <Header />
            <div className="contentCenter fullWidthHeight">
                <section className="newSetupCard">
                    <div className="cardBody" style={{ textAlign: "center" }}>
                        <p
                            style={{
                                margin: "auto",
                                marginBottom: "1rem",
                                fontSize: 18,
                            }}
                        >
                            Upload a previously exported setup to view.
                        </p>

                        {error && <p style={{ color: "red" }}>{error}</p>}

                        <OutlineButton
                            onClick={handleButtonClick}
                            variant="contained"
                            startIcon={<CloudUploadOutlinedIcon className="icon" />}
                        >
                            Upload Scene
                        </OutlineButton>

                        <VisuallyHiddenInput
                            ref={fileInputRef}
                            type="file"
                            accept=".json"
                            onChange={handleFileUpload}
                        />
                    </div>
                </section>
            </div>
            <Footer />
        </div>
    );
};

export default ImportPage;
